import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import { ReportModel } from '../../models/report.model';
import { WidgetID, WidgetModel } from '../../models/widget.model';
import { PageComposerModel } from '../../models/page-composer.model';
import { DASHBOARD_PAGE } from '../../../common/constants';
import { FilterButtonFilterObject } from 'common/components/widgets/widget-subcomponents/DashboardWidgetFilterButton';

// identifier
export const DASHBOARD_EDITOR_SLICE_ID = 'dashboardEditor';

// default state
const initialState: ReportModel = {
  id: null,
  name: 'New Dashboard Title',
  description: 'New Dashboard Description',
  composition: {
    widgets: [],
    columns: 12,
    gap: {
      columns: 16,
      rows: 16,
    },
    waitForDatasetQuestion: false,
    pageClassName: 'new-dashboard',
    pageTitle: 'New Dashboard Title',
    pageName: 'new-dashboard',
  },
  activeWidget: null,
  created_at: new Date().toJSON(),
  updated_at: new Date().toJSON(),
  isSubscribeAvailable: false,
  isSubscribed: false,
  overrideDateFilter: false,
};

//slice
const dashboardEditorSlice = createSlice({
  name: DASHBOARD_EDITOR_SLICE_ID,
  initialState: { ...initialState },
  reducers: {
    setName: (state: ReportModel, action: PayloadAction<string>) => {
      state.name = action.payload;
      state.composition.pageTitle = action.payload;
      state.composition.pageClassName = String(action.payload)
        .normalize('NFKD') // split accented characters into their base characters and diacritical marks
        .replace(/[\u0300-\u036f]/g, '') // remove all the accents, which happen to be all in the \u03xx UNICODE block.
        .trim() // trim leading or trailing whitespace
        .toLowerCase() // convert to lowercase
        .replace(/[^a-z0-9 -]/g, '') // remove non-alphanumeric characters
        .replace(/\s+/g, '-') // replace spaces with hyphens
        .replace(/-+/g, '-'); // remove consecutive hyphens
    },
    setDescription: (state: ReportModel, action: PayloadAction<string>) => {
      state.description = action.payload;
    },
    setComposition: (state: ReportModel, action: PayloadAction<PageComposerModel>) => {
      state.composition = action.payload;
      state.composition.pageClassName = String(state.name + DASHBOARD_PAGE);
      const composition = Object.assign({}, action.payload);
      composition.pageClassName = String(state.name)
        .normalize('NFKD') // split accented characters into their base characters and diacritical marks
        .replace(/[\u0300-\u036f]/g, '') // remove all the accents, which happen to be all in the \u03xx UNICODE block.
        .trim() // trim leading or trailing whitespace
        .toLowerCase() // convert to lowercase
        .replace(/[^a-z0-9 -]/g, '') // remove non-alphanumeric characters
        .replace(/\s+/g, '-') // replace spaces with hyphens
        .replace(/-+/g, '-'); // remove consecutive hyphens
      state.composition = composition;
    },
    addWidget: (state: ReportModel, action: PayloadAction<WidgetModel>) => {
      const composition = { ...state.composition };
      composition.widgets.push(action.payload);
      state.composition = composition;
    },
    updateWidget: (state: ReportModel, action: PayloadAction<WidgetModel>) => {
      const index = state.composition.widgets.findIndex(({ id }) => id === action.payload.id);
      if (index > -1) {
        const nextWidgets = [...state.composition.widgets];
        nextWidgets[index] = action.payload;
        const composition = { ...state.composition };
        composition.widgets = nextWidgets;
        state.composition = composition;
      }
    },
    removeWidget: (state: ReportModel, action: PayloadAction<WidgetID>): void => {
      const index = state.composition.widgets.findIndex(({ id }) => id === action.payload);
      if (index > -1) {
        const nextWidgets = [...state.composition.widgets];
        nextWidgets.splice(index, 1);
        const composition = { ...state.composition };
        composition.widgets = nextWidgets;
        state.composition = composition;
      }
    },
    reorderWidgets: (): /* state: ReportModel,
      action: PayloadAction<{ a: WidgetID; b: WidgetID }>*/
    void => {},
    resetEditor: (state: ReportModel): void => {
      Object.assign(state, initialState);
    },
    setActiveWidget: (state: ReportModel, action: PayloadAction<WidgetModel>): void => {
      state.activeWidget = action.payload;
    },
    setReport: (state: ReportModel, action: PayloadAction<ReportModel>): void => {
      state.id = action.payload.id;
      state.composition = action.payload.composition;
      state.created_at = action.payload.created_at;
      state.updated_at = action.payload.updated_at;
      state.name = action.payload.name;
      state.description = action.payload.description;
      state.isSubscribeAvailable = action.payload.isSubscribeAvailable;
      state.isSubscribed = action.payload.isSubscribed;
      state.universalFilters = action.payload.universalFilters;
      state.universalStartDate = action.payload.universalStartDate;
      state.universalEndDate = action.payload.universalEndDate;
      state.universalVisualDataType = action.payload.universalVisualDataType;
      state.universalVisualData = action.payload.universalVisualData;
      state.overrideDateFilter = action.payload.overrideDateFilter;
    },
    updateCompositionWidgets: (state: ReportModel, action: PayloadAction<WidgetModel[]>): void => {
      state.composition.widgets = action.payload;
    },
    updateUniversalFilters: (
      state: ReportModel,
      action: PayloadAction<FilterButtonFilterObject>
    ): void => {
      state.universalFilters = action.payload.filters;
      state.universalStartDate = action.payload.startDate;
      state.universalEndDate = action.payload.endDate;
      state.universalVisualDataType = action.payload.visualDataType;
      state.universalVisualData = action.payload.visualData;
      state.overrideDateFilter = action.payload.overrideDateFilter || false;
    },
    updateOverrideDateFilter: (state: ReportModel, action: PayloadAction<boolean>): void => {
      state.overrideDateFilter = action.payload;
    },
  },
});

//selectors
export const selectName = (state: RootState) => state.dashboardEditor.name;
export const selectDescription = (state: RootState) => state.dashboardEditor.description;
export const selectComposition = (state: RootState) => state.dashboardEditor.composition;
export const selectId = (state: RootState) => state.dashboardEditor.id;

export const selectActiveWidget = (state: RootState) => state.dashboardEditor.activeWidget;

export const selectReport = (state: RootState) => state.dashboardEditor;

export const selectReportId = (state: RootState) => state.dashboardEditor.id;

export const selectUniversalFilters = (state: RootState) => state.dashboardEditor.universalFilters;

export const selectOverrideDateFilter = (state: RootState) =>
  state.dashboardEditor.overrideDateFilter;

//actions
export const {
  addWidget,
  updateWidget,
  setDescription,
  setName,
  reorderWidgets,
  removeWidget,
  resetEditor,
  setActiveWidget,
  setComposition,
  setReport,
  updateCompositionWidgets,
  updateUniversalFilters,
  updateOverrideDateFilter,
} = dashboardEditorSlice.actions;

//reducer
export default dashboardEditorSlice;
